import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';

import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';

import {showAlertWithTimeout} from '../reducers/alerts';
import {setCodeEditorValue, toggleLock} from '../reducers/code';

import {STAGE_DISPLAY_SIZES} from '../lib/layout-constants.js';
import {getLanguageFromDeviceType} from '../lib/device';

import {diffLines} from '../lib/diff-utils';

import HardwareComponent from '../components/hardware/hardware.jsx';
import styles from '../components/hardware/hardware.css';

class Hardware extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleCodeEditorWillMount',
            'handleCodeEditorDidMount',
            'handleCodeEditorChange',
            'handleClickCodeEditorLock',
            'updateDecorations'
        ]);
        this.updateDecorations = debounce(this.updateDecorations, 500);
    }

    handleCodeEditorWillMount (monaco) {
        this.monaco = monaco;
        monaco.editor.defineTheme('readOnlyTheme', {
            base: 'vs',
            inherit: true,
            rules: [{background: 'F9F9F9'}],
            colors: {
                'editor.background': '#F9F9F9'
            }
        });
    }

    handleCodeEditorDidMount (editor) {
        this.editor = editor;
        // Close the alert message from editor
        const messageContribution = editor.getContribution(
            'editor.contrib.messageController'
        );
        if (messageContribution) {
            messageContribution.dispose();
        }

        editor.onDidAttemptReadOnlyEdit(() => {
            this.props.onCodeEditorIsLocked();
        });
    }

    componentDidUpdate (prevProps) {
        if (prevProps.isCodeEditorLocked && !this.props.isCodeEditorLocked) {
            // Unlocked
            this.originalCode = this.props.codeEditorValue;
            if (this.editor) {
                this.decorations = this.editor.deltaDecorations(this.decorations || [], []);
            }
        } else if (!prevProps.isCodeEditorLocked && this.props.isCodeEditorLocked) {
            // Locked
            this.originalCode = null;
            if (this.editor) {
                this.decorations = this.editor.deltaDecorations(this.decorations || [], []);
            }
        }
    }

    updateDecorations (newValue) {
        if (!this.monaco || !this.editor || !this.originalCode) return;

        const changes = diffLines(this.originalCode, newValue);
        const decorations = [];
        let lineNumber = 1;

        changes.forEach(part => {
            if (part.added) {
                decorations.push({
                    range: new this.monaco.Range(lineNumber, 1, lineNumber + part.count - 1, 1),
                    options: {
                        isWholeLine: true,
                        className: styles.modifiedLine,
                        overviewRuler: {
                            color: 'rgba(156, 39, 176, 0.8)',
                            position: this.monaco.editor.OverviewRulerLane.Full
                        }
                    }
                });
                lineNumber += part.count;
            } else if (part.removed) {
                // Do nothing for removed lines regarding current line count
            } else {
                lineNumber += part.count;
            }
        });

        this.decorations = this.editor.deltaDecorations(this.decorations || [], decorations);
    }

    handleCodeEditorChange (newValue) {
        this.props.onSetCodeEditorValue(newValue);
        if (!this.props.isCodeEditorLocked) {
            this.updateDecorations(newValue);
        }
    }

    handleClickCodeEditorLock () {
        if (this.props.userTier !== 'pro') return;
        this.props.onToggleCodeEditorLock();
    }

    render () {
        const codeEditorLanguage = getLanguageFromDeviceType(this.props.deviceType);
        const {
            ...props
        } = this.props;
        const editorBaseOptions = {
            overviewRulerLanes: 3,
            minimap: {enabled: false},
            automaticLayout: true,
            lineNumbers: 'on',
            lineNumbersMinChars: 3,
            glyphMargin: false,
            folding: true,
            lineDecorationsWidth: 5,
            fontSize: 13,
            scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10
            }
        };

        return (
            <HardwareComponent
                codeEditorLanguage={codeEditorLanguage}
                codeEditorOptions={this.props.isCodeEditorLocked ? 
                    {...editorBaseOptions, readOnly: true} : 
                    {...editorBaseOptions, readOnly: false}}
                codeEditorTheme={this.props.isCodeEditorLocked ? 'readOnlyTheme' : 'vs'}
                codeEditorValue={this.props.codeEditorValue}
                isCodeEditorLocked={this.props.isCodeEditorLocked}
                onCodeEditorWillMount={this.handleCodeEditorWillMount}
                onCodeEditorDidMount={this.handleCodeEditorDidMount}
                onCodeEditorChange={this.handleCodeEditorChange}
                onClickCodeEditorLock={this.props.onToggleCodeEditorLock}
                userTier={this.props.userTier}
                {...props}
            />
        );
    }
}

Hardware.propTypes = {
    codeEditorValue: PropTypes.string,
    deviceType: PropTypes.string,
    isCodeEditorLocked: PropTypes.bool.isRequired,
    onCodeEditorIsLocked: PropTypes.func.isRequired,
    onSetCodeEditorValue: PropTypes.func.isRequired,
    onToggleCodeEditorLock: PropTypes.func.isRequired,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    userTier: PropTypes.string
};

const mapStateToProps = state => ({
    codeEditorValue: state.scratchGui.code.codeEditorValue,
    deviceType: state.scratchGui.device.deviceType,
    isCodeEditorLocked: state.scratchGui.code.isCodeEditorLocked,
    userTier: state.scratchGui.tier.tier
});

const mapDispatchToProps = dispatch => ({
    onCodeEditorIsLocked: () => showAlertWithTimeout(dispatch, 'codeEditorIsLocked'),
    onSetCodeEditorValue: value => {
        dispatch(setCodeEditorValue(value));
    },
    onToggleCodeEditorLock: () => dispatch(toggleLock())
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(Hardware);
